import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProjectPermissionConstants } from "../../../../../../constants";
import { SelectData } from "../../../../../../models";
import { getCurrentUserProjectPermissions, searchProjects } from "../../../../../../service/query";
import { Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { useProjectAccessInvite, UseProjectAccessInviteReturnData } from "../../../../../../utils";
import { getSettingsProjectAccessRoute } from "../../../../../../utils/routes";
import { Toast } from "../../../../../../widget";

interface useInviteFormReturnData extends UseProjectAccessInviteReturnData {
  projectUuid?: string;
  projectOptions: SelectData;
  onProjectChange: (projectUuid: string) => void;
  onProjectSearch: (search: string) => void;
  onCancel: () => void;
}

export const useInviteForm = (): useInviteFormReturnData => {
  const navigate = useNavigate();
  const { currentUserType, currentOrganisationUuid } = useAuth();

  const [projectUuid, setProjectUuid] = useState<string>();
  const [projectSearch, setProjectSearch] = useState<string>("");
  const [projectOptions, setProjectOptions] = useState<SelectData>([]);
  const [canManageAccess, setCanManageAccess] = useState<boolean>(false);

  const navigateToProjectAccess = (): void => {
    navigate(getSettingsProjectAccessRoute(currentUserType));
  };

  // Only projects this organisation is the responsible owner of can be invited onto from here.
  useEffect(() => {
    let cancelled = false;

    const loadProjects = async (): Promise<void> => {
      if (!currentOrganisationUuid) return;

      const response = await searchProjects({
        paging: { limit: 10, beforeCursor: "", afterCursor: "" },
        sort: [],
        filter: {
          results: {
            displayName: { operator: "startsWith", value: projectSearch },
            responsibleOwnerOrganisationUuid: { operator: "eq", value: currentOrganisationUuid },
          },
        },
      });

      if (cancelled) return;

      if (response.status !== Status.Success || !response.data?.results) {
        Toast.error({ message: "Couldn't load projects - please try again later." });
        return;
      }

      setProjectOptions(
        response.data.results.flatMap((el) =>
          el.uuid && el.displayName ? { key: el.uuid, value: el.displayName } : []
        )
      );
    };

    loadProjects();

    return () => {
      cancelled = true;
    };
  }, [projectSearch, currentOrganisationUuid]);

  // The make-responsible-owner gate, read per project as ProjectContext does on the project page.
  useEffect(() => {
    let cancelled = false;

    const loadPermissions = async (): Promise<void> => {
      setCanManageAccess(false);
      if (!projectUuid) return;

      const response = await getCurrentUserProjectPermissions({ projectUuids: [projectUuid], groupUuids: null });

      if (cancelled) return;

      if (response.status !== Status.Success || !response.data) {
        Toast.error({ message: "Couldn't load your permissions for this project - please try again later." });
        return;
      }

      setCanManageAccess(
        response.data.some((permissions) =>
          permissions.currentUserPermissions.includes(ProjectPermissionConstants.MANAGE_PROJECT_ORGANISATION_INVITATION)
        )
      );
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [projectUuid]);

  const invite = useProjectAccessInvite({
    projectUuid,
    canManageAccess,
    // Every project on offer is one this organisation owns.
    currentResponsibleOwnerName: "your organisation",
    onSent: navigateToProjectAccess,
  });

  // The swap answer is about this project's developer, so a new project clears it.
  // Typing a search reports "" here, which withdraws the project.
  const onProjectChange = (value: string): void => {
    const nextProjectUuid = value || undefined;
    if (nextProjectUuid === projectUuid) return;

    setProjectUuid(nextProjectUuid);
    invite.onSelectionChange({ ...invite.selection, existingDeveloperAction: null });
  };

  return {
    ...invite,
    projectUuid,
    projectOptions,
    onProjectChange,
    onProjectSearch: setProjectSearch,
    onCancel: navigateToProjectAccess,
  };
};
